import { useCallback, useMemo } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { download } from '@utils/download';
import { ExportIcon } from '@icons/ExportIcon';
import { Loader } from '@components/Loader';
import { Button } from '@components/Button';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { EpisodeDetailsInfoBadge } from '@insights_module/components/episode_details_screen/EpisodeDetailsItemInfoBadge';
import { useEpisodeDetailsData } from '@insights_module/hooks/useEpisodeDetailsData';

const ContentWrapper = tw.div`flex px-[24px] py-[16px] items-start content-start flex-wrap gap-[12px]`;
const LoaderWrapper = tw.div`flex justify-center items-center h-[200px] w-full`;
const DownloadButton = tw(Button.Primary)`flex justify-center items-center min-h-[40px] px-[14px]`;

export const EpisodesKeywordsItem = () => {
  const { selectedEpisodeDetails, isLoading } = useEpisodeDetailsData();
  const { activeView } = useEpisodeDetailsContext();

  const top10Keywords = useMemo(
    () => selectedEpisodeDetails?.keywords?.slice(0, 10),
    [selectedEpisodeDetails?.keywords]
  );

  if (activeView !== 'keywords') return null;

  return (
    <ContentWrapper>
      {isLoading && (
        <LoaderWrapper>
          <Loader size='lg' theme='dark' />
        </LoaderWrapper>
      )}
      {top10Keywords?.map((item) => (
        <EpisodeDetailsInfoBadge
          key={item.keyword}
          label={item.keyword}
          value={Math.round(item.score).toString()}
        />
      ))}
    </ContentWrapper>
  );
};

export const DownloadKeywordsButton = () => {
  const { t } = useTranslation();
  const { selectedEpisode } = useInsightsContext();
  const { selectedEpisodeDetails, isLoading } = useEpisodeDetailsData();
  const { activeView } = useEpisodeDetailsContext();

  const handleClickDownload = useCallback(() => {
    if (isLoading || !selectedEpisodeDetails?.keywords) return;
    const file = new Blob(
      [selectedEpisodeDetails.keywords.map((item) => `${item.keyword} ${item.score}`).join('\n')],
      {
        type: 'text/plain',
      }
    );
    const fileName = `keywords-${selectedEpisode}.txt`;

    download(file, fileName);
  }, [selectedEpisodeDetails?.keywords]);

  if (activeView !== 'keywords') return null;

  return (
    <DownloadButton
      onClick={handleClickDownload}
      disabled={isLoading || !selectedEpisodeDetails?.keywords?.length}>
      <ExportIcon tw='stroke-[#fff]' />
      {t('insights.episode_details_drawer.download_button.keywords')}
    </DownloadButton>
  );
};
